import { useCallback, useRef, useState } from 'react';
import { fetchDevicePropertiesBulk, DeviceProperty } from '../../../services/api';
import { ALL_PROPERTIES } from '../surveillanceShared';

/**
 * Håller reda på vilka mätvärden varje enhet har.
 * Anropa ensureLoaded(ieees) för de enheter som behövs; hooken hämtar bara
 * dem som inte redan finns eller håller på att hämtas.
 */
export function useDeviceProperties() {
    // ieee → nycklar (t.ex. "temperature", "p_40004"). [] = hämtad men tom (eller misslyckad).
    const [propertiesMap, setPropertiesMap] = useState<Record<string, string[]>>({});
    // ieee → fulla detaljer (enhet, uppräkningsvärden, senaste värde) för regelformuläret.
    const [detailsMap, setDetailsMap] = useState<Record<string, DeviceProperty[]>>({});
    const [loading, setLoading] = useState<Set<string>>(new Set());
    // nyckel → läsbart namn, samlat från alla hämtade enheter.
    const [labelMap, setLabelMap] = useState<Record<string, string>>({});

    // Speglar state synkront så att ensureLoaded kan vara stabil och ändå
    // inte hämta samma enhet två gånger.
    const knownRef = useRef<Set<string>>(new Set());
    const loadingRef = useRef<Set<string>>(new Set());

    const ensureLoaded = useCallback((ieees: string[]) => {
        const missing = ieees.filter(i => i && !knownRef.current.has(i) && !loadingRef.current.has(i));
        if (missing.length === 0) return;
        for (const i of missing) loadingRef.current.add(i);
        setLoading(new Set(loadingRef.current));

        fetchDevicePropertiesBulk(missing).then(grouped => {
            const keys: Record<string, string[]> = {};
            const details: Record<string, DeviceProperty[]> = {};
            const labels: Record<string, string> = {};
            for (const ieee of missing) {
                const result = grouped ? (grouped[ieee] ?? []) : [];
                keys[ieee] = result.map(p => p.key);
                details[ieee] = result;
                for (const p of result) { if (p.label) labels[p.key] = p.label; }
                knownRef.current.add(ieee);
                loadingRef.current.delete(ieee);
            }
            setPropertiesMap(prev => ({ ...prev, ...keys }));
            setDetailsMap(prev => ({ ...prev, ...details }));
            if (Object.keys(labels).length > 0) setLabelMap(prev => ({ ...prev, ...labels }));
            setLoading(new Set(loadingRef.current));
        });
    }, []);

    /** Nycklar för en enhet, sorterade. [] när inget hämtats än – se isLoading. */
    const propertiesForDevice = useCallback((ieee: string): string[] => {
        if (!ieee) return ALL_PROPERTIES;
        const props = propertiesMap[ieee];
        if (!props) return []; // hämtas – visa inte en annan enhets lista
        return props.length > 0 ? props.slice().sort() : [];
    }, [propertiesMap]);

    const propertyDetailsForDevice = useCallback((ieee: string): DeviceProperty[] => {
        if (!ieee) return [];
        return detailsMap[ieee] ?? [];
    }, [detailsMap]);

    /** Sant tills enhetens mätvärden finns i cachen. */
    const isLoading = useCallback((ieee: string): boolean =>
        !!ieee && (loading.has(ieee) || propertiesMap[ieee] === undefined),
    [loading, propertiesMap]);

    /** Sant om någon av enheterna håller på att hämtas just nu. */
    const isAnyLoading = useCallback((ieees: Iterable<string>): boolean =>
        Array.from(ieees).some(ieee => loading.has(ieee)),
    [loading]);

    return { propertiesMap, labelMap, ensureLoaded, propertiesForDevice, propertyDetailsForDevice, isLoading, isAnyLoading };
}
